import { useEffect, useRef } from 'react';
import { HistoryModel, ExportOptions, ExportMode } from '../../types/models';
import { ExportState, PreviewState } from '../../stores/chatManagementStore';
import { ChatDetailsPanel } from './panels/ChatDetailsPanel';
import { ExportPanel } from './panels/ExportPanel';

// ─── EmptyChatList ────────────────────────────────────────────────────────────

function EmptyChatList({ noChats = false }: { noChats: boolean }) {
	return (
		<div className="flex items-center justify-center h-full text-neutral-400">
			<div className="text-center p-lg">
				<i className="fas fa-comments text-4xl mb-md text-neutral-400" />
				{noChats ? (
					<>
						<p className="text-lg font-medium mb-xs">
							No chat history found
						</p>
						<p className="text-md font-normal text-neutral-400">
							Start a conversation to see your chat history here
						</p>
					</>
				) : (
					<>
						<p className="text-lg font-medium mb-xs">
							No chat selected
						</p>
						<p className="text-md font-normal text-neutral-400">
							Please select a chat from the list to view details
						</p>
					</>
				)}
			</div>
		</div>
	);
}

// ─── RightPanel ───────────────────────────────────────────────────────────────

interface RightPanelProps {
	mode: ExportMode;
	options: ExportOptions;
	exportState: ExportState;
	selectedChatData: HistoryModel | null;
	selectedChatCount: number;
	selectedChatMessages: number | null;
	selectedChatImages: number | null;
	isLoadingMessages: boolean;
	previewState: PreviewState;
	chatId: string;
	selectedChats: string[];
	onExportSelected: () => void;
	onCancelExport: () => void;
	onExportSingle: (chatId: string) => void;
	onTriggerExport: (chatId: string) => void;
	onOpenChat: (chatId: string) => void;
	onOptionsChange: (options: ExportOptions) => void;
	onAddToExport: (chatId: string) => void;
	onRemoveFromExport: (chatId: string) => void;
	onExportOnly: (chatId: string) => void;
}

function RightPanel({
	mode,
	options,
	exportState,
	selectedChatData,
	selectedChatCount,
	selectedChatMessages,
	selectedChatImages,
	isLoadingMessages,
	previewState,
	chatId,
	selectedChats,
	onExportSelected,
	onCancelExport,
	onExportSingle,
	onTriggerExport,
	onOpenChat,
	onOptionsChange,
	onAddToExport,
	onRemoveFromExport,
	onExportOnly,
}: RightPanelProps) {
	const scrollRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		scrollRef.current?.scrollTo({ top: 0 });
	}, [selectedChatData?.id]);

	const getPanelTitle = (): string => {
		switch (mode) {
			case 'bulk':
				return 'Bulk Export';
			case 'single':
				return 'Export Preview';
			default:
				return 'Chat Details';
		}
	};

	const renderPanelContent = () => {
		if (mode === 'bulk') {
			const focusedChatId = selectedChatData?.id;
			return (
				<ExportPanel
					mode="bulk"
					options={options}
					exportState={exportState}
					previewState={previewState}
					selectedCount={selectedChatCount}
					focusedChatTitle={selectedChatData?.title}
					isFocusedChatChecked={
						focusedChatId
							? selectedChats.includes(focusedChatId)
							: false
					}
					onExport={onExportSelected}
					onCancel={onCancelExport}
					onAddToExport={() =>
						focusedChatId && onAddToExport(focusedChatId)
					}
					onRemoveFromExport={() =>
						focusedChatId && onRemoveFromExport(focusedChatId)
					}
					onExportOnly={() =>
						focusedChatId && onExportOnly(focusedChatId)
					}
				/>
			);
		}

		if (mode === 'single' && selectedChatData) {
			return (
				<ExportPanel
					mode="single"
					options={options}
					exportState={exportState}
					previewState={previewState}
					chat={selectedChatData}
					messageCount={selectedChatMessages}
					imageCount={selectedChatImages}
					isLoadingMessages={isLoadingMessages}
					onExport={() => onExportSingle(selectedChatData.id)}
					onCancel={onCancelExport}
					onOptionsChange={onOptionsChange}
				/>
			);
		}

		if (selectedChatData) {
			return (
				<ChatDetailsPanel
					chat={selectedChatData}
					messageCount={selectedChatMessages}
					imageCount={selectedChatImages}
					isLoadingMessages={isLoadingMessages}
					isCurrentChat={chatId === selectedChatData.id}
					onOpen={() => onOpenChat(selectedChatData.id)}
					onExport={() => onTriggerExport(selectedChatData.id)}
				/>
			);
		}

		return <EmptyChatList noChats={false} />;
	};

	return (
		<div className="w-1/2 flex flex-col overflow-hidden">
			<h3 className="text-sm font-medium text-neutral-100 mb-sm uppercase tracking-wide">
				{getPanelTitle()}
			</h3>
			<div
				ref={scrollRef}
				className="flex-1 overflow-y-auto bg-neutral-600 border border-neutral-400 rounded-medium p-md"
			>
				{renderPanelContent()}
			</div>
		</div>
	);
}

export { EmptyChatList, RightPanel };
